import {
  CategoryResponse,
  MaterialResponse,
  ProductKind,
} from "@/web-client/api";
import { SearchQueryParameterController } from "@/hooks/useSearchQueryParameter";
import { useMemo } from "react";
import { FC, useState } from "react";
import { ProductSearchQueryParameter } from "@/types/ProductSearchQueryParameter";
import SearchDropdownButton from "@/components/SearchDropdownButton";
import styled from "styled-components";
import Colors from "@/consts/Colors";
import ColorGridTile from "@/components/ColorGridTile";
import tailwindConfig from "../../tailwind.config.js";
import { useSeparatedCategories } from "@/hooks/useCategories";
import useMaterials from "@/hooks/useMaterials";
import EntityDrillDown from "@/components/EntityDrillDown";
import useFindEntityWithSubEntities from "@/hooks/useFindEntityWithSubEntitites";

interface Props {
  categories: CategoryResponse[];
  query: ProductSearchQueryParameter;
  queryController?: SearchQueryParameterController<ProductSearchQueryParameter>;
  onOpenModal: () => void;
  kind?: ProductKind;
}

enum ProductDropdown {
  None = -1,
  Category = 1,
  Color = 2,
  Material = 3,
}

const SearchProductDropdowns: FC<Props> = ({
  categories,
  query,
  queryController,
  onOpenModal,
  kind,
}): JSX.Element => {
  const [select, setSelect] = useState(ProductDropdown.None);
  const materials = useMaterials();
  const isCategory = useMemo(
    () => select === ProductDropdown.Category,
    [select],
  );
  const isMaterial = useMemo(
    () => select === ProductDropdown.Material,
    [select],
  );
  const isColor = useMemo(() => select === ProductDropdown.Color, [select]);

  const { selectedEntity: selectedCategory } =
    useFindEntityWithSubEntities<CategoryResponse>(
      categories,
      query.category_id,
    );

  const {
    interiorCategories,
    materialCategories,
    hasInteriorCategories,
    hasMaterialCategories,
  } = useSeparatedCategories(categories);

  const hasMultiCategories = useMemo(
    () => hasInteriorCategories && hasMaterialCategories,
    [hasInteriorCategories, hasMaterialCategories],
  );

  const closeHandler = () => setSelect(ProductDropdown.None);

  const splitedColors = useMemo<string[]>(() => {
    const value = query.colors;

    return value ? value.split(",") : [];
  }, [query]);

  const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    > * {
      + * {
        border-left: 1px solid ${tailwindConfig.theme.colors.gray[200]};
      }
    }
  `;

  const interiorCategoriesList = useMemo(() => {
    return (
      <>
        {hasInteriorCategories && (
          <EntityDrillDown<MaterialResponse, ProductSearchQueryParameter>
            entities={interiorCategories}
            watchQueryParameter={"category_id"}
            query={query}
            queryController={queryController}
            onReachedBottomEntity={closeHandler}
          />
        )}
      </>
    );
  }, [
    hasMultiCategories,
    hasInteriorCategories,
    interiorCategories,
    query,
    queryController,
  ]);

  const materialCategoriesList = useMemo(() => {
    return (
      <>
        {hasMaterialCategories && (
          <EntityDrillDown<MaterialResponse, ProductSearchQueryParameter>
            entities={materialCategories}
            watchQueryParameter={"category_id"}
            query={query}
            queryController={queryController}
            onReachedBottomEntity={closeHandler}
          />
        )}
      </>
    );
  }, [
    hasMultiCategories,
    hasMaterialCategories,
    materialCategories,
    query,
    queryController,
  ]);

  const selectedProductKind = useMemo<ProductKind>(() => {
    if (!selectedCategory) return ProductKind.INTERIOR;

    return selectedCategory.kind;
  }, [selectedCategory]);

  const onlyInterior = useMemo<boolean>(
    () => ProductKind.INTERIOR === kind,
    [kind],
  );
  const onlyBuildingMaterial = useMemo<boolean>(
    () => ProductKind.BUILDING_MATERIAL === kind,
    [kind],
  );

  return (
    <div>
      <Grid>
        <SearchDropdownButton
          active={isCategory}
          label={"カテゴリ"}
          onClick={() =>
            setSelect(
              isCategory ? ProductDropdown.None : ProductDropdown.Category,
            )
          }
        />
        <SearchDropdownButton
          active={isColor}
          label={"カラー"}
          onClick={() =>
            setSelect(isColor ? ProductDropdown.None : ProductDropdown.Color)
          }
        />
        <SearchDropdownButton
          active={isMaterial}
          label={"素材"}
          onClick={() =>
            setSelect(
              isMaterial ? ProductDropdown.None : ProductDropdown.Material,
            )
          }
        />
        <SearchDropdownButton
          noarrow={true}
          active={false}
          label={"その他"}
          onClick={() => {
            closeHandler();
            onOpenModal();
          }}
        />
      </Grid>

      {select !== ProductDropdown.None && (
        <div>
          {isCategory && selectedProductKind === ProductKind.INTERIOR && (
            <>
              {interiorCategoriesList}
              {!onlyInterior && materialCategoriesList}
            </>
          )}

          {isCategory &&
            selectedProductKind === ProductKind.BUILDING_MATERIAL && (
              <>
                {materialCategoriesList}
                {!onlyBuildingMaterial && interiorCategoriesList}
              </>
            )}

          {isColor && (
            <div className="grid grid-cols-4 bg-white">
              {Colors.map((color) => {
                const selected = splitedColors.includes(color.id.toString());

                return (
                  <button
                    key={color.id}
                    onClick={() => {
                      queryController.addQuery(
                        "colors",
                        selected
                          ? splitedColors
                              .filter((v) => v !== color.id.toString())
                              .join(",")
                          : [...splitedColors, color.id.toString()].join(","),
                      );
                      queryController.submitQuery();
                    }}
                  >
                    <div style={{ backgroundColor: selected ? "#f1f1f1" : "" }}>
                      <ColorGridTile color={color} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {isMaterial && (
            <EntityDrillDown<MaterialResponse, ProductSearchQueryParameter>
              entities={materials}
              watchQueryParameter={"material_id"}
              query={query}
              queryController={queryController}
              onReachedBottomEntity={closeHandler}
            />
          )}
        </div>
      )}
    </div>
  );
};
export default SearchProductDropdowns;
